import { 
  doc, 
  updateDoc, 
  getDoc, 
  arrayUnion, 
  increment,
  collection,
  query,
  where,
  getDocs
} from 'firebase/firestore';
import { db } from '../firebase/config';

class QuestionHistoryService {
  
  /**
   * Record a question as asked for a user
   * @param {string} userId - User ID
   * @param {string} questionId - Question ID
   * @param {Object} performance - Performance data
   */
  async recordQuestionAsked(userId, questionId, performance = {}) {
    try {
      console.log(`📝 Recording question ${questionId} for user ${userId}`);
      
      const userProgressRef = doc(db, 'userProgress', userId);
      
      const updateData = {
        'questionHistory.askedQuestions': arrayUnion(questionId),
        'questionHistory.lastAskedDate': new Date(),
        'questionHistory.totalQuestionsAnswered': increment(1),
        'questionHistory.updatedAt': new Date()
      };
      
      // Update performance metrics
      if (performance.isCorrect !== undefined) {
        updateData['questionHistory.correctAnswers'] = increment(performance.isCorrect ? 1 : 0);
      }
      
      if (performance.responseTime) {
        // Update average response time
        const userDoc = await getDoc(userProgressRef);
        const currentData = userDoc.data();
        const currentAvg = currentData?.questionHistory?.averageResponseTime || 0;
        const totalAnswered = currentData?.questionHistory?.totalQuestionsAnswered || 0;
        
        const newAvg = ((currentAvg * totalAnswered) + performance.responseTime) / (totalAnswered + 1);
        updateData['questionHistory.averageResponseTime'] = newAvg;
      }
      
      // Update question-specific performance
      if (performance.isCorrect !== undefined || performance.responseTime) {
        updateData[`questionHistory.questionPerformance.${questionId}`] = {
          isCorrect: performance.isCorrect,
          responseTime: performance.responseTime,
          answeredAt: new Date()
        };
      }
      
      await updateDoc(userProgressRef, updateData);
      console.log(`✅ Question ${questionId} recorded for user ${userId}`);
      
    } catch (error) {
      console.error('❌ Error recording question asked:', error);
      throw error;
    }
  }
  
  /**
   * Get user's question history
   * @param {string} userId - User ID
   * @returns {Object} Question history data
   */
  async getUserQuestionHistory(userId) {
    try {
      const userProgressRef = doc(db, 'userProgress', userId);
      const userDoc = await getDoc(userProgressRef);
      
      if (!userDoc.exists()) {
        console.log(`❌ User progress not found for ${userId}`);
        return null;
      }
      
      const userData = userDoc.data();
      return userData.questionHistory || {
        askedQuestions: [],
        questionPerformance: {},
        lastAskedDate: null,
        totalQuestionsAnswered: 0,
        correctAnswers: 0,
        averageResponseTime: 0
      };
      
    } catch (error) {
      console.error('❌ Error getting user question history:', error);
      throw error;
    }
  }
  
  /**
   * Check if user has seen a specific question
   * @param {string} userId - User ID
   * @param {string} questionId - Question ID
   * @returns {boolean} Whether user has seen the question
   */
  async hasUserSeenQuestion(userId, questionId) {
    try {
      const history = await this.getUserQuestionHistory(userId);
      return history.askedQuestions.includes(questionId);
    } catch (error) {
      console.error('❌ Error checking if user has seen question:', error);
      return false;
    }
  }
  
  /**
   * Get questions user hasn't seen for a specific learning node
   * @param {string} userId - User ID
   * @param {string} learningNodeId - Learning node ID
   * @returns {Array} Array of unasked questions
   */
  async getUnaskedQuestionsForNode(userId, learningNodeId) {
    try {
      console.log(`🔍 Getting unasked questions for node ${learningNodeId} and user ${userId}`);
      
      // Get user's question history
      const history = await this.getUserQuestionHistory(userId);
      const askedQuestions = history.askedQuestions || [];
      
      // Get all questions for this learning node
      const questionsRef = collection(db, 'questions');
      const q = query(
        questionsRef,
        where('learningNodeId', '==', learningNodeId),
        where('questionType', '==', 'original')
      );
      
      const snapshot = await getDocs(q);
      const allQuestions = snapshot.docs.map(doc => ({
        questionId: doc.id,
        ...doc.data()
      }));
      
      // Filter out questions user has already seen
      const unaskedQuestions = allQuestions.filter(q => 
        !askedQuestions.includes(q.questionId)
      );
      
      console.log(`   - Total questions for node: ${allQuestions.length}`);
      console.log(`   - Unasked questions: ${unaskedQuestions.length}`);
      
      return unaskedQuestions;
      
    } catch (error) {
      console.error('❌ Error getting unasked questions for node:', error);
      return [];
    }
  }
  
  /**
   * Get user's performance statistics
   * @param {string} userId - User ID
   * @returns {Object} Performance statistics
   */
  async getUserPerformanceStats(userId) {
    try {
      const history = await this.getUserQuestionHistory(userId);
      
      const totalAnswered = history.totalQuestionsAnswered || 0;
      const correctAnswers = history.correctAnswers || 0;
      const averageResponseTime = history.averageResponseTime || 0;
      
      return {
        totalQuestionsAnswered: totalAnswered,
        correctAnswers: correctAnswers,
        incorrectAnswers: totalAnswered - correctAnswers,
        accuracy: totalAnswered > 0 ? (correctAnswers / totalAnswered) * 100 : 0,
        averageResponseTime: averageResponseTime,
        lastAskedDate: history.lastAskedDate
      };
      
    } catch (error) {
      console.error('❌ Error getting user performance stats:', error);
      return {
        totalQuestionsAnswered: 0,
        correctAnswers: 0,
        incorrectAnswers: 0,
        accuracy: 0,
        averageResponseTime: 0,
        lastAskedDate: null
      };
    }
  }
  
  /**
   * Get questions that need review (haven't been asked recently)
   * @param {string} userId - User ID
   * @param {Array} completedNodeIds - Array of completed learning node IDs
   * @param {number} daysThreshold - Days threshold for review (default: 3)
   * @returns {Array} Questions that need review
   */
  async getQuestionsNeedingReview(userId, completedNodeIds, daysThreshold = 3) {
    try {
      console.log(`🔄 Getting questions needing review for user ${userId}`);
      
      const history = await this.getUserQuestionHistory(userId);
      const questionPerformance = history.questionPerformance || {};
      const now = new Date();
      
      const questionsNeedingReview = [];
      
      for (const nodeId of completedNodeIds) {
        const unaskedQuestions = await this.getUnaskedQuestionsForNode(userId, nodeId);
        
        // If no unasked questions, check performance of asked questions
        if (unaskedQuestions.length === 0) {
          const allQuestions = await this.getAllQuestionsForNode(nodeId);
          
          for (const question of allQuestions) {
            const performance = questionPerformance[question.questionId];
            
            if (performance) {
              const answeredAt = performance.answeredAt?.toDate?.() || new Date(performance.answeredAt);
              const daysSinceAnswered = (now - answeredAt) / (1000 * 60 * 60 * 24);
              
              // Question needs review if:
              // 1. Answered incorrectly, OR
              // 2. Haven't been asked in threshold days
              if (!performance.isCorrect || daysSinceAnswered >= daysThreshold) {
                questionsNeedingReview.push(question);
              }
            }
          }
        } else {
          // Add unasked questions
          questionsNeedingReview.push(...unaskedQuestions);
        }
      }
      
      console.log(`   - Found ${questionsNeedingReview.length} questions needing review`);
      return questionsNeedingReview;
      
    } catch (error) {
      console.error('❌ Error getting questions needing review:', error);
      return [];
    }
  }
  
  /**
   * Get all questions for a learning node
   * @param {string} learningNodeId - Learning node ID
   * @returns {Array} All questions for the node
   */
  async getAllQuestionsForNode(learningNodeId) {
    try {
      const questionsRef = collection(db, 'questions');
      const q = query(
        questionsRef,
        where('learningNodeId', '==', learningNodeId),
        where('questionType', '==', 'original')
      );
      
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        questionId: doc.id,
        ...doc.data()
      }));
      
    } catch (error) {
      console.error('❌ Error getting all questions for node:', error);
      return [];
    }
  }
  
  /**
   * Reset user's question history (for testing purposes)
   * @param {string} userId - User ID
   */
  async resetUserQuestionHistory(userId) {
    try {
      console.log(`🔄 Resetting question history for user ${userId}`);
      
      const userProgressRef = doc(db, 'userProgress', userId);
      
      const resetData = {
        questionHistory: {
          askedQuestions: [],
          questionPerformance: {},
          lastAskedDate: null,
          totalQuestionsAnswered: 0,
          correctAnswers: 0,
          averageResponseTime: 0
        },
        updatedAt: new Date()
      };
      
      await updateDoc(userProgressRef, resetData);
      console.log(`✅ Question history reset for user ${userId}`);
      
    } catch (error) {
      console.error('❌ Error resetting user question history:', error);
      throw error;
    }
  }
}

export default new QuestionHistoryService();
